import React, { useMemo, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import { Link, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from 'expo-router/react-navigation';
import { useTranslation } from 'react-i18next';
import { CommunityThemeScope } from '@/components/ui/CommunityThemeScope';
import { CommunityCode, communities, isCommunityCode } from '@/constants/communities';
import { NotFoundContent } from '@/components/ui/NotFoundContent';
import { Button, ButtonText } from '@/components/gluestack/button';
import { VStack } from '@/components/gluestack/vstack';
import { Text } from '@/components/gluestack/text';
import { useOpenWebPage } from '@/hooks/useOpenWebPage';
import { useLanguage } from '@/hooks/useLanguage';
import { useToastMessages } from '@/hooks/useToastMessages';
import WebView from 'react-native-webview';
import { createCommunitiesHeader } from '@/components/layout/CommunitiesHeader';
import { HStack } from '~/components/gluestack/hstack';
import CommunityHeroBanner from '@/components/ui/CommunityHeroBanner';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { LinkSquare02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { WebDocumentTitle } from '@/components/layout/WebDocumentTitle';

export interface ILegacyAccountPageProps {
  /** Path beginning with /Account, e.g. '/Account/Register' or '/Account/LostPassword' */
  accountPath: '/Account/Register' | '/Account/LostPassword';
  /** Title key to display in header and page text */
  titleText: string;
  /** Web helper text displayed on web, describes the redirect */
  webHelperText: string;
  /** Optional text to show in a success toast once navigation back to sign-in completes */
  successText?: string;
}

export default function LegacyAccountPage({
  accountPath,
  titleText,
  webHelperText,
  successText,
}: ILegacyAccountPageProps) {
  const { community } = useLocalSearchParams<{ community?: CommunityCode }>();
  const communityCode = isCommunityCode(community) ? community : undefined;
  const { t } = useTranslation();
  const router = useRouter();
  const navigation = useNavigation();
  const openWebPage = useOpenWebPage();
  const { currentLanguage } = useLanguage();
  const { showSuccessToast } = useToastMessages();

  const targetUrl = useMemo(() => {
    if (!communityCode) return '';
    const communityEntity = communities[communityCode];
    const culture = encodeURIComponent(currentLanguage);
    if (Platform.OS === 'web') {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const returnUrl = encodeURIComponent(`${origin}/sign-in`);
      return `https://www.${communityEntity.domain}${accountPath}?returnUrl=${returnUrl}&culture=${culture}`;
    }
    return `https://www.${communityEntity.domain}${accountPath}?inApp=true&culture=${culture}`;
  }, [accountPath, communityCode, currentLanguage]);

  const webViewRef = useRef<WebView>(null);

  if (!communityCode) {
    return (
      <>
        <WebDocumentTitle title={titleText} />
        <Stack.Screen
          options={{
            header: createCommunitiesHeader({
              title: titleText,
              backHref: '/communities',
              rightIconMode: false,
              rightNeutral: true,
              hideBackOnRoot: false,
              showRightIcons: false,
            }),
          }}
        />
        <NotFoundContent />
      </>
    );
  }

  // Native: render WebView and intercept relevant navigations
  if (Platform.OS !== 'web') {
    return (
      <>
        <WebDocumentTitle title={titleText} appName={communities[communityCode].name} />
        <Stack.Screen
          options={{
            header: createCommunitiesHeader({
              title: titleText,
              backHref: '/communities',
              rightIconMode: true,
              rightNeutral: true,
              hideBackOnRoot: false,
              showRightIcons: false,
            }),
          }}
        />
        <CommunityThemeScope community={communityCode}>
          <View style={{ flex: 1, backgroundColor: 'white' }}>
            <WebView
              ref={webViewRef}
              source={{ uri: targetUrl }}
              onShouldStartLoadWithRequest={(request) => {
                const url = (request.url || '').toLowerCase();
                // Intercept only the routes we explicitly handle; allow all other navigations
                if (url.includes('/account/logon')) {
                  // If previous route in stack includes "sign-in", go back; otherwise replace
                  let shouldGoBackToSignIn = false;
                  try {
                    const state = navigation?.getState?.();
                    let currentState: any = state;
                    // Walk to the deepest active state
                    while (currentState?.routes?.[currentState.index ?? 0]?.state) {
                      currentState = currentState.routes[currentState.index ?? 0].state;
                    }
                    const currentIndex = currentState?.index ?? 0;
                    const prevRoute = currentState?.routes?.[currentIndex - 1];
                    const prevName = prevRoute?.name?.toLowerCase?.();
                    const prevPathname = prevRoute?.params?.pathname?.toLowerCase?.();
                    shouldGoBackToSignIn =
                      !!prevName?.includes('sign-in') || !!prevPathname?.includes('sign-in');
                  } catch {}
                  const maybeShowToast = () => {
                    if (successText) {
                      // small timeout to ensure route transition is complete before showing toast
                      setTimeout(() => showSuccessToast(successText, undefined, 'top'), 50);
                    }
                  };
                  if (shouldGoBackToSignIn && navigation?.canGoBack?.()) {
                    router.back();
                  } else {
                    router.replace({
                      pathname: '/communities/[community]/sign-in',
                      params: { community: communityCode },
                    });
                  }
                  maybeShowToast();
                  return false;
                }
                if (url.includes('/servicedesk/')) {
                  openWebPage(request.url);
                  return false;
                }
                return true;
              }}
              startInLoadingState
              javaScriptEnabled
              domStorageEnabled
              originWhitelist={['*']}
            />
          </View>
        </CommunityThemeScope>
      </>
    );
  }

  // Web helper UI
  return (
    <>
      <WebDocumentTitle title={titleText} appName={communities[communityCode].name} />
      <Stack.Screen
        options={{
          header: createCommunitiesHeader({
            title: titleText,
            backHref: '/communities',
            rightIconMode: false,
            rightNeutral: true,
            hideBackOnRoot: false,
            showRightIcons: true,
          }),
        }}
      />
      <CommunityThemeScope community={communityCode}>
        <View className="w-full flex-1 self-center bg-background-0 p-8">
          <VStack className="mx-auto w-full items-center justify-center gap-4 pb-24 sm:max-w-[440px] ">
            <View className="mb-4 aspect-[1248/702] w-full overflow-hidden rounded-lg">
              <CommunityHeroBanner
                community={communityCode as any}
                contentFit="contain"
                className="h-full w-full"
              />
            </View>
            <Text size="lg" className="font-semibold">
              {titleText}
            </Text>
            <Text className="text-center ">{webHelperText}</Text>
            <HStack className="mt-4 gap-4">
              <ButtonWrapper
                icon={LinkSquare02Icon}
                text={t('common.continue')}
                onPress={() => {
                  try {
                    window.open(targetUrl, '_blank');
                  } catch {}
                  router.replace('/sign-in' as any);
                }}
              />
              <Link replace href={'/sign-in' as any} asChild>
                <Button variant="outline">
                  <ButtonText>{t('common.cancel')}</ButtonText>
                </Button>
              </Link>
            </HStack>
          </VStack>
        </View>
      </CommunityThemeScope>
    </>
  );
}
